import React, { useState, useEffect, useReducer, useCallback, useContext } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import InputBase from "@material-ui/core/InputBase";
import Typography from "@material-ui/core/Typography";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import ScheduleModal from "../../components/ScheduleModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import moment from "moment";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import { Calendar, momentLocalizer } from "react-big-calendar";
import "moment/locale/pt-br";
import "react-big-calendar/lib/css/react-big-calendar.css";
import SearchIcon from "@material-ui/icons/Search";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import AddIcon from "@material-ui/icons/Add";

import "./Schedules.css";

// Defina a função getUrlParam antes de usá-la
function getUrlParam(paramName) {
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get(paramName);
}

const eventTitleStyle = {
  fontSize: "14px",
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
};

const localizer = momentLocalizer(moment);
var defaultMessages = {
  date: "Data",
  time: "Hora",
  event: "Evento",
  allDay: "Dia Todo",
  week: "Semana",
  work_week: "Agendamentos",
  day: "Dia",
  month: "Mês",
  previous: "Anterior",
  next: "Próximo",
  yesterday: "Ontem",
  tomorrow: "Amanhã",
  today: "Hoje",
  agenda: "Agenda",
  noEventsInRange: "Não há agendamentos no período.",
  showMore: function showMore(total) {
    return "+" + total + " mais";
  }
};

const reducer = (state, action) => {
  if (action.type === "LOAD_SCHEDULES") {
    const schedules = action.payload;
    const newSchedules = [];

    const newState = [...state];

    schedules.forEach((schedule) => {
      const scheduleIndex = newState.findIndex((s) => s.id === schedule.id);
      if (scheduleIndex !== -1) {
        newState[scheduleIndex] = schedule;
      } else {
        newSchedules.push(schedule);
      }
    });

    return [...newState, ...newSchedules];
  }

  if (action.type === "UPDATE_SCHEDULES") {
    const schedule = action.payload;
    const scheduleIndex = state.findIndex((s) => s.id === schedule.id);

    if (scheduleIndex !== -1) {
      const newState = [...state];
      newState[scheduleIndex] = schedule;
      return newState;
    } else {
      return [schedule, ...state];
    }
  }

  if (action.type === "DELETE_SCHEDULE") {
    const scheduleId = action.payload;

    const scheduleIndex = state.findIndex((s) => s.id === scheduleId);
    if (scheduleIndex !== -1) {
      const newState = [...state];
      newState.splice(scheduleIndex, 1);
      return newState;
    }
    return state;
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    backgroundColor: theme.palette.type === 'light' ? '#f5f5f5' : theme.palette.background.default,
    padding: theme.spacing(4),
    overflow: "hidden",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(4),
  },
  titleContainer: {
    display: "flex",
    flexDirection: "column",
  },
  title: {
    fontWeight: 800,
    color: theme.palette.primary.main,
    fontSize: "2.125rem",
    lineHeight: 1.235,
    marginBottom: theme.spacing(0.5),
  },
  subtitle: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  searchContainer: {
    display: "flex",
    alignItems: "center",
    backgroundColor: theme.palette.background.paper,
    borderRadius: "50px",
    padding: "6px 20px",
    width: "300px",
    boxShadow: "0px 2px 4px -1px rgba(0,0,0,0.2)",
    border: `1px solid ${theme.palette.divider}`,
    transition: "all 0.3s ease",
    "&:focus-within": {
      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
      borderColor: theme.palette.primary.main,
    },
  },
  searchInput: {
    marginLeft: theme.spacing(1),
    flex: 1,
    fontSize: "0.95rem",
  },
  searchIcon: {
    color: theme.palette.text.secondary,
  },
  addButton: {
    borderRadius: "50px",
    padding: "8px 24px",
    fontWeight: "bold",
    textTransform: "none",
    boxShadow: theme.shadows[3],
    "&:hover": {
      boxShadow: theme.shadows[6],
    },
  },
  calendarPaper: {
    flex: 1,
    padding: theme.spacing(2), // Increased padding for better look
    overflowY: "auto",
    borderRadius: "24px", // More rounded
    backgroundColor: theme.palette.background.paper,
    boxShadow: "none",
    border: `1px solid ${theme.palette.divider}`,
    ...theme.scrollbarStyles,
    "&::-webkit-scrollbar": {
      width: "8px",
      height: "8px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "rgba(0, 0, 0, 0.2)",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "transparent",
    },
  },
  calendarToolbar: {
    height: "100%",
    // Toolbar Container
    '& .rbc-toolbar': {
      marginBottom: theme.spacing(3),
      flexWrap: "wrap",
      gap: theme.spacing(1),
    },
    // Toolbar Label (Month/Date)
    '& .rbc-toolbar-label': {
      fontSize: "1.5rem",
      fontWeight: 700,
      color: theme.palette.text.primary,
    },
    // Button Groups
    '& .rbc-btn-group': {
      boxShadow: "none",
      border: "none",
      gap: "8px",
      display: "flex", // Allows buttons to be detached
    },
    // Individual Buttons
    '& .rbc-btn-group button': {
      color: theme.palette.text.secondary,
      backgroundColor: theme.palette.type === 'light' ? "#f0f2f5" : "rgba(255,255,255,0.05)",
      border: "none",
      borderRadius: "50px !important", // Force pill shape
      padding: "8px 20px",
      fontSize: "0.9rem",
      fontWeight: 600,
      textTransform: "capitalize",
      boxShadow: "none",
      transition: "all 0.2s ease",
      cursor: "pointer",
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
        color: theme.palette.text.primary,
        transform: "translateY(-1px)",
      },
      '&:active': {
        boxShadow: "none",
      },
      '&:focus': {
        outline: "none",
      },
      // Active (Selected) State
      '&.rbc-active': {
        color: "#fff",
        backgroundColor: `${theme.palette.primary.main} !important`,
        boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
        '&:hover': {
          backgroundColor: theme.palette.primary.dark,
        }
      },
    },
    // Header (Days of week)
    '& .rbc-header': {
      padding: "16px 0",
      fontWeight: 700,
      textTransform: "uppercase",
      fontSize: "0.85rem",
      letterSpacing: "1px",
      color: theme.palette.text.secondary,
      borderBottom: `2px solid ${theme.palette.divider}`, // Thicker divider
      borderLeft: "none", // Cleaner look
    },
    // Month View
    '& .rbc-month-view': {
      borderRadius: "16px",
      border: `1px solid ${theme.palette.divider}`,
      overflow: "hidden",
    },
    // Grid Setup
    '& .rbc-day-bg + .rbc-day-bg': {
      borderLeft: `1px solid ${theme.palette.divider}`,
    },
    '& .rbc-month-row + .rbc-month-row': {
      borderTop: `1px solid ${theme.palette.divider}`,
    },
    '& .rbc-off-range-bg': { // Background for days not in current month
      backgroundColor: theme.palette.type === 'light' ? "#f9f9f9" : "rgba(0,0,0,0.1)",
    },
    // Events
    '& .rbc-event': {
      borderRadius: "8px",
      padding: "2px 5px",
      backgroundColor: theme.palette.primary.main,
      border: "none",
      boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
    },
    // Agenda View Styling
    '& .rbc-agenda-view table.rbc-agenda-table': {
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: "16px",
      borderCollapse: "separate", // Needed for radius
      overflow: "hidden",
      '& thead > tr > th': {
        padding: "16px",
        borderBottom: `2px solid ${theme.palette.divider}`,
      },
      '& tbody > tr > td': {
        padding: "16px",
        borderBottom: `1px solid ${theme.palette.divider}`,
        fontSize: "0.95rem",
      },
      '& tbody > tr:last-child > td': {
        borderBottom: "none",
      },
      '& tbody > tr:hover': {
        backgroundColor: theme.palette.action.hover,
      }
    },
    '& .rbc-agenda-time-cell': {
      textTransform: "lowercase",
      fontWeight: "bold",
      color: theme.palette.text.secondary,
    },
    '& .rbc-agenda-date-cell': {
      fontWeight: "bold",
      color: theme.palette.text.primary,
    }
  },
}));

const Schedules = () => {
  const classes = useStyles();
  const history = useHistory();

  //   const socketManager = useContext(SocketContext);
  const { user, socket } = useContext(AuthContext);


  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [deletingSchedule, setDeletingSchedule] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [schedules, dispatch] = useReducer(reducer, []);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [contactId, setContactId] = useState(+getUrlParam("contactId"));

  const { getPlanCompany } = usePlans();

  useEffect(() => {
    async function fetchData() {
      const companyId = user.companyId;
      const planConfigs = await getPlanCompany(undefined, companyId);
      if (!planConfigs.plan.useSchedules) {
        toast.error("Esta empresa não possui permissão para acessar essa página! Estamos lhe redirecionando.");
        setTimeout(() => {
          history.push(`/`)
        }, 1000);
      }
    }
    fetchData();
  }, [user, history, getPlanCompany]);

  const fetchSchedules = useCallback(async () => {
    try {
      const { data } = await api.get("/schedules", {
        params: { searchParam, pageNumber },
      });

      dispatch({ type: "LOAD_SCHEDULES", payload: data.schedules });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  }, [searchParam, pageNumber]);

  const handleOpenScheduleModalFromContactId = useCallback(() => {
    if (contactId) {
      handleOpenScheduleModal();
    }
  }, [contactId]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchSchedules();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [
    searchParam,
    pageNumber,
    contactId,
    fetchSchedules,
    handleOpenScheduleModalFromContactId,
  ]);

  useEffect(() => {
    // handleOpenScheduleModalFromContactId();
    // const socket = socketManager.GetSocket(user.companyId, user.id);


    const onCompanySchedule = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_SCHEDULES", payload: data.schedule });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_SCHEDULE", payload: +data.scheduleId });
      }
    }

    socket.on(`company${user.companyId}-schedule`, onCompanySchedule)

    return () => {
      socket.off(`company${user.companyId}-schedule`, onCompanySchedule)
    };
  }, [socket]);

  const cleanContact = () => {
    setContactId("");
  };

  const handleOpenScheduleModal = () => {
    setSelectedSchedule(null);
    setScheduleModalOpen(true);
  };

  const handleCloseScheduleModal = () => {
    setSelectedSchedule(null);
    setScheduleModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditSchedule = (schedule) => {
    setSelectedSchedule(schedule);
    setScheduleModalOpen(true);
  };

  const handleDeleteSchedule = async (scheduleId) => {
    try {
      await api.delete(`/schedules/${scheduleId}`);
      toast.success(i18n.t("schedules.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingSchedule(null);
    setSearchParam("");
    setPageNumber(1);

    dispatch({ type: "RESET" });
    setPageNumber(1);
    await fetchSchedules();
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const truncate = (str, len) => {
    if (str.length > len) {
      return str.substring(0, len) + "...";
    }
    return str;
  };

  return (
    <div className={classes.mainContainer}>
      <ConfirmationModal
        title={
          deletingSchedule &&
          `${i18n.t("schedules.confirmationModal.deleteTitle")}`
        }
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteSchedule(deletingSchedule.id)}
      >
        {i18n.t("schedules.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      {scheduleModalOpen && (
        <ScheduleModal
          open={scheduleModalOpen}
          onClose={handleCloseScheduleModal}
          reload={fetchSchedules}
          scheduleId={
            selectedSchedule ? selectedSchedule.id : null
          }
          contactId={contactId}
          cleanContact={cleanContact}
        />
      )}

      <div className={classes.header}>
        <div className={classes.titleContainer}>
          <Typography className={classes.title}>
            {i18n.t("schedules.title")} ({schedules.length})
          </Typography>
          <Typography className={classes.subtitle}>
            Gerencie seus agendamentos
          </Typography>
        </div>

        <div className={classes.headerActions}>
          <div className={classes.searchContainer}>
            <SearchIcon className={classes.searchIcon} />
            <InputBase
              placeholder={i18n.t("contacts.searchPlaceholder")}
              classes={{
                root: classes.searchInput,
              }}
              value={searchParam}
              onChange={handleSearch}
            />
          </div>

          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenScheduleModal}
            className={classes.addButton}
            startIcon={<AddIcon />}
          >
            {i18n.t("schedules.buttons.add")}
          </Button>
        </div>
      </div>

      <Paper className={classes.calendarPaper} variant="outlined" onScroll={handleScroll}>
        <Calendar
          messages={defaultMessages}
          formats={{
            agendaDateFormat: "DD/MM ddd",
            weekdayFormat: "dddd"
          }}
          localizer={localizer}
          events={schedules.map((schedule) => ({
            title: (
              <div key={schedule.id} className="event-container">
                <div style={eventTitleStyle}>{schedule?.contact?.name}</div>
                <DeleteOutlineIcon
                  onClick={() => handleDeleteSchedule(schedule.id)}
                  className="delete-icon"
                />
                <EditIcon
                  onClick={() => {
                    handleEditSchedule(schedule);
                    setScheduleModalOpen(true);
                  }}
                  className="edit-icon"
                />
              </div>
            ),
            start: new Date(schedule.sendAt),
            end: new Date(schedule.sendAt),
          }))}
          startAccessor="start"
          endAccessor="end"
          style={{ height: 500 }}
          className={classes.calendarToolbar}
        />
      </Paper>
    </div>
  );
};

export default Schedules;